import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import ClientSearchInput from "../ClientSearchInput/ClientSearchInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
/* The row markers, the label colour and the list itself are
   `.client-tasks-modal-*`, defined in this sheet. Imported EXPLICITLY:
   relying on a host to have imported it means the rows render only
   because CRA concatenates stylesheets globally, and a third host would
   get unstyled markup. That invisible coupling is what this component
   was built to undo. */
import "../ClientTasksDisplay/clientTasksDisplay.css";
import "./taskDetailFields.css";

/**
 * TaskDetailFields — the body of a task's detail view, in one place.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS. The app shipped this UI TWICE: once inline on the Tasks
 * page (desktop, `AdminDashboardTasks`) and once in `TaskModal` (the phone
 * path, and the Single Client page). Same seven rows, same class names, same
 * mutation — and they had drifted in six separate ways, two of which were live
 * bugs. This is the de-duplication, not a new feature.
 *
 * IT RENDERS THE ROWS ONLY. The surrounding shell and the action row belong to
 * the host: `TaskModal` puts them in `BdrModal`'s `title`/`actions`, the inline
 * pane puts them in its own box. Those genuinely differ, so they stay out.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE ASSIGNEE INDEX IS DERIVED, NOT STORED — and that is a bug fix.
 *
 * `TaskModal` set it in an effect keyed `[usersInfo, openModal]` that read
 * `taskObj` from closure without depending on it, while `closeModal` reset the
 * task but NOT the index. The modal is persistently mounted and unkeyed, so:
 * open task A, close, open task B => the effect runs while the task is still
 * cleared, no branch matches, the stale index survives, and B renders showing
 * **A's assignee**. On a first open it was a race between the task fetch and
 * the user fetch (both network-only); if the users landed first the "System"
 * branch never ran at all.
 *
 * `useMemo` over `(assigneeId, users)` removes the race, the stale value and
 * the reset-ordering problem together. Nothing to keep in sync.
 *
 * ─────────────────────────────────────────────────────────────────
 * SENTINEL ASSIGNEE IDS — "22" is everyone, "11" is the reminder worker.
 * They are not user documents, so they sit past the end of the real users:
 * "All" at `users.length`, "System" at `users.length + 1`.
 *
 * THE TWO GUARDS are deliberate and came from `TaskModal`: a Reminder task's
 * type cannot be re-typed (the worker owns it) and a System-owned task cannot
 * be reassigned. They are INERT on the Tasks page — `getTasksForUser` can
 * never return `assigneeId: "11"` — and live on the Single Client page, which
 * does show reminder tasks. They live here so the component is correct wherever
 * it is mounted.
 *
 * `/reminder/i` deliberately has NO `g` flag: a global regex kept at module
 * scope would carry `lastIndex` between calls and `.test()` would alternate
 * true/false on successive renders.
 */

const TASK_TYPES = [
  "F/U",
  "Schedule",
  "Send Payment Receipt",
  "Appt / Invoice",
  "Add To Ledger",
  "Other",
];

const ALL_ASSIGNEE_ID = "22";
const SYSTEM_ASSIGNEE_ID = "11";
const REMINDER_RX = /reminder/i;

/*
 * A label/value ROW — the repeated half of every line below.
 *
 * DEFINED AT MODULE SCOPE, AND THAT IS LOAD-BEARING. It used to live inside
 * `TaskDetailFields`, which meant a NEW function identity on every render.
 * React reconciles by element TYPE identity, so a new type is not "the same
 * component with new props" — it unmounts the whole subtree and mounts a
 * fresh one. The Note field's <textarea> was therefore destroyed and
 * recreated on every keystroke: type one character, state changes, the
 * component re-renders, `Row` is a different type, the DOM node is replaced,
 * and focus goes with it. That is the "types one character then blurs" bug.
 *
 * It closes over nothing — only its own props — so module scope costs
 * nothing and is the whole fix.
 *
 * NEVER define a component inside another component's body.
 */
const Row = ({ label, children, stacked }) => (
  <div className={`tdf-row${stacked ? " is-stacked" : ""}`}>
    <span className="tdf-label">{label}</span>
    <span className="tdf-value">{children}</span>
  </div>
);

const TaskDetailFields = ({
  /** the draft task being edited */
  task,
  /** `getUsersNamesForTasks` rows; may be absent — see the read-only fallback */
  users,
  /** (targetLike | isoString) => void — plain field edits */
  onFieldChange,
  /** ({ assigneeId, assigneeName }) => void — resolved, never an index */
  onAssigneeChange,
  /** (clientSearchResult) => void */
  onClientPicked,
}) => {
  const navigate = useNavigate();
  const [addClientOpenSearch, setAddClientOpenSearch] = useState(false);

  /* Memoised because `users || []` would be a NEW array every render, which
     would make both memos below recompute every time and defeat the point. */
  const userRows = useMemo(() => users || [], [users]);
  const allIndex = userRows.length;
  const isReminder = REMINDER_RX.test(task?.taskType || "");
  const isSystemOwned = task?.assigneeId === SYSTEM_ASSIGNEE_ID;

  /*
   * The user list is fetched ADMIN-ONLY by the Tasks page, so for an office
   * user it is empty. A select whose value matches none of its options renders
   * blank and is unusable — which is what shipped. When there is no list to
   * choose from, show the name we already have instead of a broken control.
   */
  const selectedAssigneeIndex = useMemo(() => {
    if (task?.assigneeId === ALL_ASSIGNEE_ID) return allIndex;
    if (task?.assigneeId === SYSTEM_ASSIGNEE_ID) return allIndex + 1;
    /* -1 when the id is not in the list — a deactivated user, or anyone
       `getUsersNamesForTasks` omits. Do NOT fall back to `allIndex`: that is
       the "All" sentinel, so the select would assert a real, wrong value that
       a reader could act on. */
    return userRows.findIndex((u) => u?._id === task?.assigneeId);
  }, [task?.assigneeId, userRows, allIndex]);

  const canPickAssignee = userRows.length > 0 && selectedAssigneeIndex !== -1;

  const assigneeOptions = useMemo(
    () => [
      ...userRows.map((userInfo, index) => ({
        value: index,
        label: `${userInfo?.firstName} ${userInfo?.lastName}`.trim(),
      })),
      { value: allIndex, label: "All" },
      /* "System" only exists on a task the reminder worker owns, and that
         task's picker is disabled anyway — but without the option the select
         has no row matching its value and renders empty. */
      ...(isSystemOwned ? [{ value: allIndex + 1, label: "System" }] : []),
    ],
    [userRows, allIndex, isSystemOwned]
  );

  const handleAssigneePick = (rawValue) => {
    /* Both a native <select> and BdrSelectPopup hand back a STRING. */
    const index = parseInt(rawValue, 10);
    if (index === allIndex) {
      onAssigneeChange({ assigneeId: ALL_ASSIGNEE_ID, assigneeName: "All" });
      return;
    }
    const picked = userRows[index];
    if (!picked) return;
    onAssigneeChange({
      assigneeId: picked._id,
      assigneeName: `${picked.firstName} ${picked.lastName}`.trim(),
    });
  };

  return (
    /*
     * A label/value grid, not a bulleted list. This was `<ul>` rows with a
     * dot marker and a green label inline with the value — the labels were
     * ragged because each sat directly before its own value, so nothing in the
     * column lined up. A fixed label column fixes that for both hosts at once.
     */
    <div className="tdf-rows">
      <Row label="Client">
        {task?.associatedClientName?.length > 0 ? (
          <BdrButton
            size="sm"
            variant="action"
            onClick={() => {
              navigate(
                `/bdr-office/clients/view-client/${task?.associatedClientId}`
              );
            }}
          >
            {task?.associatedClientName}
          </BdrButton>
        ) : (
          <>
            <span className="tdf-muted">None</span>
            {/* `default` once it is open, because at that point it is a Cancel
                — intent picks the variant, never the colour. */}
            <BdrButton
              size="sm"
              variant={addClientOpenSearch ? "default" : "action"}
              onClick={() => setAddClientOpenSearch((prev) => !prev)}
            >
              {addClientOpenSearch ? "Cancel" : "Add"}
            </BdrButton>
          </>
        )}
      </Row>

      {addClientOpenSearch && (
        <Row label="" stacked>
          <ClientSearchInput
            setWidth={"100%"}
            returnResults={(result) => {
              setAddClientOpenSearch(false);
              onClientPicked(result);
            }}
          />
        </Row>
      )}

      <Row label="Task Type">
        <BdrInput
          className="task-detail-inline-select"
          name="taskType"
          value={task?.taskType}
          disabled={isReminder}
          /* "Reminder" is offered only when the task already IS one — it is set
             by the reminder worker, never chosen here. */
          options={isReminder ? [...TASK_TYPES, "Reminder"] : TASK_TYPES}
          onChange={(event) => onFieldChange(event.target)}
        />
      </Row>

      <Row label="Status">
        <span className={task?.active ? "" : "tdf-muted"}>
          {task?.active ? "Active" : "Completed"}
        </span>
      </Row>

      <Row label="Assigned to">
        {canPickAssignee ? (
          <BdrInput
            className="task-detail-inline-select"
            name="user-task-assignee-update"
            value={selectedAssigneeIndex}
            disabled={isSystemOwned}
            options={assigneeOptions}
            onChange={(event) => handleAssigneePick(event.target.value)}
          />
        ) : (
          <span>{task?.assigneeName || "Unassigned"}</span>
        )}
      </Row>

      <Row label="Created">
        <span className="tdf-muted">{BAGroupTimeFormat(task?.dateCreated)}</span>
      </Row>

      <Row label="Due">
        <span className="tdf-due">{BAGroupTimeFormat(task?.dueDate)}</span>
        <EventDateTimeInput
          value={task?.dueDate}
          onChange={onFieldChange}
          hideTime={true}
        />
      </Row>

      <Row label="Note" stacked>
        <BdrInput
          name="note"
          value={task?.note}
          onChange={(event) => onFieldChange(event.target)}
          multiline
          rows={3}
        />
      </Row>
    </div>
  );
};

export default TaskDetailFields;
